import { Component, OnInit } from '@angular/core';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { catchError, switchMap, take, tap } from 'rxjs';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { FormFieldModule, TextBoxModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FormErrorComponent } from '../../shared/components/form-error/form-error.component';
import { oneUppercaseCharacterValidator } from '../../shared/directives/form-validators/one-uppercase-character.directive';
import { oneLowercaseCharacterValidator } from '../../shared/directives/form-validators/one-lowercase-character.directive';
import { oneDigitCharacterValidator } from '../../shared/directives/form-validators/one-digit-character.directive';
import { oneSpecialCharacterValidator } from '../../shared/directives/form-validators/one-special-character.directive';
import { compareFieldsValidator } from '../../shared/directives/form-validators/compare-fields.directive';
import { AuthorizationService } from '../../services/authorization/authorization.service';
import { notAllowedCharactersValidator } from '../../shared/directives/form-validators/not-allowed-characters.directive';
import { RoutingService } from '../../services/routing/routing.service';
import { Router } from '@angular/router';
import { AfRoutes } from '../../routes';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'af-confirm-sign-in',
  imports: [
    ButtonModule,
    CommonModule,
    FormFieldModule,
    LabelModule,
    ReactiveFormsModule,
    TextBoxModule,
    FormErrorComponent,
  ],
  templateUrl: './set-password.component.html',
  styleUrl: './set-password.component.scss',
})
export class SetPasswordComponent implements OnInit {
  onChangePasswordRoute = false;
  formAction: undefined | (() => void)
  confirmPasswordForm: FormGroup = new FormGroup(
    {
      oldPassword: new FormControl(
        '',
        this.onChangePasswordRoute ? [Validators.required] : []
      ),
      newPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(12),
        oneUppercaseCharacterValidator(),
        oneLowercaseCharacterValidator(),
        oneDigitCharacterValidator(),
        oneSpecialCharacterValidator(),
        notAllowedCharactersValidator(),
      ]),
      confirmPassword: new FormControl(''),
    },
    [compareFieldsValidator('newPassword', 'confirmPassword')]
  );

  errorMessage?: string;
  validatorErrors: { [key: string]: boolean } = {};

  constructor(
    private authenticationService: AuthenticationService,
    private authorizationService: AuthorizationService,
    private routingService: RoutingService,
    private router: Router
  ) {
    this.onChangePasswordRoute = this.router.url.replace('/', '') === AfRoutes.changePassword
  }

  ngOnInit() {
    console.log(this.onChangePasswordRoute)
    this.updateValidatorErrors();
    this.confirmPasswordForm.valueChanges
      .pipe(
        tap(() => {
          this.clearErrorMessage();
          this.updateValidatorErrors();
        })
      )
      .subscribe();
  }

  submitForm(): void {
    if(this.onChangePasswordRoute) {
      this.changePassword()
    } else {
      this.confirmSignIn()
    }
  }

  changePassword(): void {
    if (this.confirmPasswordForm.valid) {
      const newPassword = this.confirmPasswordForm.get('newPassword')?.value;
      const oldPassword = this.confirmPasswordForm.get('oldPassword')?.value;
      const confirmPassword =
        this.confirmPasswordForm.get('confirmPassword')?.value;
      if (newPassword === confirmPassword) {
        this.authenticationService
          .changePassword({ confirmPassword, newPassword, oldPassword })
          .pipe(
            switchMap(() => this.authorizationService.updateRoles()),
            tap(() => this.routingService.navigateToDashboard()),
            take(1),
            catchError((error) => {
              this.errorMessage = error.message;
              throw error;
            })
          )
          .subscribe();
      }
    }
  }

  confirmSignIn(): void {
    if (this.confirmPasswordForm.valid) {
      const newPassword = this.confirmPasswordForm.get('newPassword')?.value;
      const confirmPassword =
        this.confirmPasswordForm.get('confirmPassword')?.value;
      if (newPassword === confirmPassword) {
        this.authenticationService
          .confirmUserSignIn(newPassword)
          .pipe(
            switchMap(() => this.authorizationService.updateRoles()),
            tap(() => this.routingService.navigateToDashboard()),
            take(1),
            catchError((error) => {
              this.errorMessage = error.message;
              throw error;
            })
          )
          .subscribe();
      }
    }
  }

  private clearErrorMessage(): void {
    this.errorMessage = undefined;
  }

  private updateValidatorErrors(): void {
    const newPassword = this.confirmPasswordForm.get('newPassword');
    this.validatorErrors['required'] = Boolean(
      newPassword?.errors && newPassword.errors['required']
    );
    this.validatorErrors['minlength'] =
      Boolean(newPassword?.errors && newPassword.errors['required']) ||
      Boolean(
        newPassword?.errors &&
          newPassword.errors['minlength'] &&
          newPassword.errors['minlength']['requiredLength'] !==
            newPassword.errors['minlength']['actualLength']
      );
    this.validatorErrors['oneUppercaseCharacter'] = Boolean(
      newPassword?.errors && newPassword.errors['oneUppercaseCharacter']
    );
    this.validatorErrors['oneLowercaseCharacter'] = Boolean(
      newPassword?.errors && newPassword.errors['oneLowercaseCharacter']
    );
    this.validatorErrors['oneDigitCharacter'] = Boolean(
      newPassword?.errors && newPassword.errors['oneDigitCharacter']
    );
    this.validatorErrors['oneSpecialCharacter'] = Boolean(
      newPassword?.errors && newPassword.errors['oneSpecialCharacter']
    );
    this.validatorErrors['notAllowedCharacters'] = Boolean(
      newPassword?.errors && newPassword.errors['notAllowedCharacters']
    );
    this.validatorErrors['compareFields'] =
      Boolean(newPassword?.errors && newPassword.errors['required']) ||
      Boolean(
        this.confirmPasswordForm?.errors &&
          this.confirmPasswordForm.errors['compareFields']
      );
  }
}
